import { Injectable } from '@angular/core';
import { AngularFirestore } from '@angular/fire/firestore';
import { ApplicationInsightsService, AppInsightTags } from '@app/core/services/application-insights.service';
import { ProviderService } from '@app/core/services/provider.service';
import firebase from 'firebase/app';
import 'firebase/firestore';
import { EMPTY, from, Observable } from 'rxjs';
import { catchError, map, switchMap, take } from 'rxjs/operators';
import {
  PracticeAgentAnalyticsDetails,
  PracticeAgentAnalyticsEvent,
  PracticeAgentAnalyticsEventType,
  PracticeAgentHomeTab,
  PracticeAgentSessionEndReason,
} from './practice-agent-analytics.models';
import { PracticeAgentPage } from './practice-agent-message.models';

const WIDGET_STATE_COLLECTION_ROOT = 'Experiments/PracticeAgentClinicalWidget/Practice';
const HOME_TABS: PracticeAgentHomeTab[] = ['schedule', 'tracker', 'history', 'path', 'documents', 'whatsapp', 'other'];
const SESSION_END_REASONS: PracticeAgentSessionEndReason[] = ['doctor_close', 'doctor_snooze', 'navigate_away'];

@Injectable({ providedIn: 'root' })
export class PracticeAgentAnalyticsService {
  constructor(
    private firestore: AngularFirestore,
    private providerService: ProviderService,
    private appInsightsService: ApplicationInsightsService
  ) {}

  track(
    eventType: PracticeAgentAnalyticsEventType,
    currentPage: PracticeAgentPage,
    details: PracticeAgentAnalyticsDetails = {}
  ): Observable<void> {
    return this.providerService.provider$.pipe(
      take(1),
      switchMap(provider => {
        const practiceTenantId = provider?.PracticeTenantId;
        const practiceId = provider?.PracticeId;
        if (!practiceTenantId || !practiceId) {
          return EMPTY;
        }

        const approvedDetails: PracticeAgentAnalyticsDetails = {};
        if (HOME_TABS.includes(details.HomeTab)) {
          approvedDetails.HomeTab = details.HomeTab;
        }
        if (SESSION_END_REASONS.includes(details.SessionEndReason)) {
          approvedDetails.SessionEndReason = details.SessionEndReason;
        }
        if (Number.isFinite(details.SnoozeDurationMs) && details.SnoozeDurationMs >= 0) {
          approvedDetails.SnoozeDurationMs = details.SnoozeDurationMs;
        }
        if (this.isIsoDate(details.SuppressionUntil)) {
          approvedDetails.SuppressionUntil = details.SuppressionUntil;
        }

        const event: PracticeAgentAnalyticsEvent = {
          ...approvedDetails,
          EventType: eventType,
          PracticeTenantId: practiceTenantId,
          PracticeId: practiceId,
          CurrentPage: currentPage,
          Source: 'hbc',
          SchemaVersion: 1,
          OccurredAt: firebase.firestore.FieldValue.serverTimestamp(),
        };

        return from(
          this.firestore
            .collection<PracticeAgentAnalyticsEvent>(
              `${WIDGET_STATE_COLLECTION_ROOT}/${practiceTenantId}/WidgetState/${practiceId}/Analytics`
            )
            .add(event)
        ).pipe(map(() => undefined));
      }),
      catchError(() => {
        this.appInsightsService.trackEvent(AppInsightTags.PRACTICE_AGENT_WIDGET_ANALYTICS_FAILED, {
          eventType,
        });
        return EMPTY;
      })
    );
  }

  private isIsoDate(value: unknown): value is string {
    if (typeof value !== 'string') {
      return false;
    }

    const date = new Date(value);
    return Number.isFinite(date.getTime()) && date.toISOString() === value;
  }
}
